import ProductBeauty, {
  ProductBeautyRef,
} from "@/business/myPage/productSetting/ProductBeauty";
import ProductSchool, {
  ProductSchoolRef,
} from "@/business/myPage/productSetting/ProductSchool";
import ProductDaycare, {
  ProductDaycareRef,
} from "@/business/myPage/productSetting/ProductDaycare";
import ProductHotel, {
  ProductHotelRef,
} from "@/business/myPage/productSetting/ProductHotel";
import { LayoutHeaderInButton } from "@/HeaderInButton";
import { Dialog } from "@/Dialog";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect, useMemo } from "react";
import { useBusinessServiceProvideList } from "../../../api/services/business/myPage";

export const Route = createFileRoute(
  "/business/layout/user-product-management"
)({
  component: UserSettingProductManagement,
});

function UserSettingProductManagement() {
  const [selected, setSelected] = useState("");
  const productSchoolRef = useRef<ProductSchoolRef>(null);
  const productBeautyRef = useRef<ProductBeautyRef>(null);
  const productDaycareRef = useRef<ProductDaycareRef>(null);
  const productHotelRef = useRef<ProductHotelRef>(null);
  const [isSaving, setIsSaving] = useState(false);

  // 서비스 제공 목록 API 호출
  const { data: serviceProvideData, isLoading } =
    useBusinessServiceProvideList();

  // 활성화된 서비스만 필터링
  const activeServices = useMemo(
    () =>
      serviceProvideData?.filter((service) => service.activeYn === "Y") || [],
    [serviceProvideData]
  );

  useEffect(() => {
    if (activeServices.length > 0) {
      setSelected(activeServices[0].cdProvideType);
    }
  }, [activeServices]);

  // 모달 상태 관리
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [mddalMessage, setMddalMessage] = useState({
    title: "알림",
    message1: "상품금액은 언제든지 변경 가능합니다",
    message2: "변경된 상품은 예약 확정 시 적용됩니다.",
    message3: "변경된 상품은 예약 확정 시 적용됩니다.",
  });

  // 변경사항 확인 모달 상태
  const [isChangeConfirmModalVisible, setIsChangeConfirmModalVisible] = useState(false);
  const [pendingSelected, setPendingSelected] = useState("");
  const [, setHasChanges] = useState(false);

  const showModal = (
    message1: string,
    message2: string,
    message3: string,
    title: string = "알림"
  ) => {
    setMddalMessage({ title, message1, message2, message3 });
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  // 변경사항 확인 함수
  const checkForChanges = () => {
    const selectedService = activeServices.find(
      (service) => service.cdProvideType === selected
    );

    if (selectedService?.cdProvideType === "PRODUCT_TYPE_KINDERGARTEN" && productSchoolRef.current) {
      const changedProducts = productSchoolRef.current.getChangedProducts();
      return changedProducts.length > 0;
    } else if (selectedService?.cdProvideType === "PRODUCT_TYPE_GROOMING" && productBeautyRef.current) {
      const changedProducts = productBeautyRef.current.getChangedProducts();
      return changedProducts.length > 0;
    } else if (selectedService?.cdProvideType === "PRODUCT_TYPE_DAYCARE" && productDaycareRef.current) {
      const changedProducts = productDaycareRef.current.getChangedProducts();
      return changedProducts.length > 0;
    } else if (selectedService?.cdProvideType === "PRODUCT_TYPE_HOTEL" && productHotelRef.current) {
      const changedProducts = productHotelRef.current.getChangedProducts();
      return changedProducts.length > 0;
    }
    return false;
  };

  // 선택 변경 처리 함수
  const handleSelectedChange = (newSelected: string) => {
    const changes = checkForChanges();
    
    if (changes) {
      setHasChanges(true);
      setPendingSelected(newSelected);
      setIsChangeConfirmModalVisible(true);
    } else {
      setSelected(newSelected);
    }
  };

  // 변경사항 확인 모달 닫기
  const handleChangeConfirmModalClose = () => {
    setIsChangeConfirmModalVisible(false);
    setSelected(pendingSelected); // 클릭한 탭으로 이동
    setHasChanges(false);
  };

  // 변경사항 저장 후 이동
  const handleSaveAndMove = async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const selectedService = activeServices.find(
        (service) => service.cdProvideType === selected
      );

      let saveResult = { success: false, message: "" };

      if (selectedService?.cdProvideType === "PRODUCT_TYPE_KINDERGARTEN" && productSchoolRef.current) {
        saveResult = await productSchoolRef.current.saveProducts();
      } else if (selectedService?.cdProvideType === "PRODUCT_TYPE_GROOMING" && productBeautyRef.current) {
        saveResult = await productBeautyRef.current.saveProducts();
      } else if (selectedService?.cdProvideType === "PRODUCT_TYPE_DAYCARE" && productDaycareRef.current) {
        saveResult = await productDaycareRef.current.saveProducts();
      } else if (selectedService?.cdProvideType === "PRODUCT_TYPE_HOTEL" && productHotelRef.current) {
        saveResult = await productHotelRef.current.saveProducts();
      }

      if (saveResult.success) {
        setSelected(pendingSelected);
        showModal(
          "상품 금액은 언제든지 변경 가능합니다.",
          "신규 서비스 예약부터 적용되며,",
          "기존 예약은 적용되지 않습니다. (예약 변경 포함)",
          "저장되었습니다."
        );
      } else {
        showModal(saveResult.message, "", "", "알림");
      }
    } catch (error) {
      console.error("저장 실패:", error);
      showModal("저장 중 오류가 발생했습니다.", "", "", "오류");
    } finally {
      setIsSaving(false);
      handleChangeConfirmModalClose();
    }
  };

  const handleSave = async () => {
    if (isSaving || activeServices.length === 0) return;

    setIsSaving(true);
    try {
      const selectedService = activeServices.find(
        (service) => service.cdProvideType === selected
      );

      if (
        selectedService?.cdProvideType === "PRODUCT_TYPE_KINDERGARTEN" &&
        productSchoolRef.current
      ) {
        const result = await productSchoolRef.current.saveProducts();

        if (result.success) {
          showModal(
            "상품 금액은 언제든지 변경 가능합니다.",
            "신규 서비스 예약부터 적용되며,",
            "기존 예약은 적용되지 않습니다. (예약 변경 포함)",
            "저장되었습니다."
          );
        } else {
          showModal(result.message, "", "", "알림");
        }
      } else if (
        selectedService?.cdProvideType === "PRODUCT_TYPE_GROOMING" &&
        productBeautyRef.current
      ) {
        const result = await productBeautyRef.current.saveProducts();

        if (result.success) {
          showModal(
            "상품 금액은 언제든지 변경 가능합니다.",
            "신규 서비스 예약부터 적용되며,",
            "기존 예약은 적용되지 않습니다. (예약 변경 포함)",
            "저장되었습니다."
          );
        } else {
          showModal(result.message, "", "", "알림");
        }
      } else if (
        selectedService?.cdProvideType === "PRODUCT_TYPE_DAYCARE" &&
        productDaycareRef.current
      ) {
        const result = await productDaycareRef.current.saveProducts();

        if (result.success) {
          showModal(
            "상품 금액은 언제든지 변경 가능합니다.",
            "신규 서비스 예약부터 적용되며,",
            "기존 예약은 적용되지 않습니다. (예약 변경 포함)",
            "저장되었습니다."
          );
        } else {
          showModal(result.message, "", "", "알림");
        }
      } else if (
        selectedService?.cdProvideType === "PRODUCT_TYPE_HOTEL" &&
        productHotelRef.current
      ) {
        const result = await productHotelRef.current.saveProducts();

        if (result.success) {
          showModal(
            "상품 금액은 언제든지 변경 가능합니다.",
            "신규 서비스 예약부터 적용되며,",
            "기존 예약은 적용되지 않습니다. (예약 변경 포함)",
            "저장되었습니다."
          );
        } else {
          showModal(result.message, "", "", "알림");
        }
      } else {
        showModal("지원하지 않는 서비스 타입입니다.", "", "", "알림");
      }
    } catch (error) {
      console.error("저장 실패:", error);
      showModal("저장 중 오류가 발생했습니다.", "", "", "오류");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <LayoutHeaderInButton title={"상품관리"} hasBackBtn hasFootPrint={false}>
        <button
          className={`text-sm ${isSaving ? "text-gray-300" : "text-[#666666]"}`}
          onClick={handleSave}
          disabled={isSaving}
        >
          저장
        </button>
      </LayoutHeaderInButton>
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#F5F5F5]">
        <div className="flex gap-2 justify-center px-2 pt-5">
          {isLoading ? (
            <div className="text-center text-gray-500">로딩 중...</div>
          ) : activeServices.length > 0 ? (
            activeServices.map((service) => (
              <button
                key={service.cdProvideType}
                className={`px-5 py-1.5 rounded-full border font-medium text-[#666666] w-full
                ${
                  selected === service.cdProvideType
                    ? "border-orange-400 text-orange-500"
                    : "border-gray-300 text-gray-500"
                }
                bg-white`}
                style={
                  selected === service.cdProvideType ? { borderWidth: 1 } : {}
                }
                onClick={() => handleSelectedChange(service.cdProvideType)}
              >
                {service.productName}
              </button>
            ))
          ) : (
            <div className="text-center text-gray-500">
              사용 가능한 서비스가 없습니다.
            </div>
          )}
        </div>
        <div className="flex-auto">
          {activeServices.length > 0 &&
            selected &&
            (() => {
              const selectedService = activeServices.find(
                (service) => service.cdProvideType === selected
              );
              switch (selectedService?.cdProvideType) {
                case "PRODUCT_TYPE_KINDERGARTEN":
                  return <ProductSchool ref={productSchoolRef} />;
                case "PRODUCT_TYPE_GROOMING":
                  return <ProductBeauty ref={productBeautyRef} />;
                case "PRODUCT_TYPE_DAYCARE":
                  return <ProductDaycare ref={productDaycareRef} />;
                case "PRODUCT_TYPE_HOTEL":
                  return <ProductHotel ref={productHotelRef} />;
                default:
                  return (
                    <div className="p-4 text-center text-gray-500">
                      지원하지 않는 서비스 타입입니다.
                    </div>
                  );
              }
            })()}
        </div>
      </div>

      {/* 저장 결과 메시지 모달 */}
      <Dialog
        visible={isModalVisible}
        onClose={handleModalClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              {mddalMessage.title}
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              {mddalMessage.message1}
            </div>
            <div className="text-sm text-center text-[#666666]">
              {mddalMessage.message2}
            </div>
            <div className="text-sm text-center text-[#666666]">
              {mddalMessage.message3}
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleModalClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 변경사항 확인 모달 */}
      <Dialog
        visible={isChangeConfirmModalVisible}
        onClose={handleChangeConfirmModalClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              변경사항이 있습니다
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              변경된 내용이 있습니다.
            </div>
            <div className="text-sm text-center text-[#666666]">
              저장하고 이동하시겠습니까?
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-gray-600 bg-gray-200 rounded-full border border-[#E5E5E5]"
              onClick={handleChangeConfirmModalClose}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleSaveAndMove}
              disabled={isSaving}
            >
              {isSaving ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

// const products = [
//   {
//     code: "beauty",
//     type: "미용 비용설정",
//     products: [
//       {
//         type: "product",
//         name: "상품1",
//         price: 10000,
//       },
//       {
//         type: "product",
//         name: "상품1",
//         price: 10000,
//       },
//     ],
//   },
//     {
//     code: "beauty2",
//     type: "미용 비용설정2",
//     products: [
//       {
//         type: "product",
//         name: "상품1",
//         price: 10000,
//       },
//       {
//         type: "product",
//         name: "상품1",
//         price: 10000,
//       },
//     ],
//   },
// ]
